import React, { useMemo, useState } from "react";
import { Button, Descriptions, Drawer, Modal, Space, Tag, Typography } from "antd";
import { ApartmentOutlined, CompressOutlined, EditOutlined, ExpandOutlined } from "@ant-design/icons";
import { formatEnumLabel } from "../constants";
import useFormattedDate from "hooks/useFormatDate";
import { STORAGE_BASE_URL } from "constants";

export default function BusinessLocationDetailsDrawer({
  open,
  onClose,
  location,
  onEdit,
}) {
  const title = useMemo(() => {
    if (!location?.name) return "Business Location";
    return location.name;
  }, [location]);
  const { formatDateTime } = useFormattedDate();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [fullWidth, setFullWidth] = useState(false);

  const resolveFileUrl = (value) => {
    if (!value) return null;
    const str = String(value);
    if (str.includes("://")) return str;
    return `${STORAGE_BASE_URL}/files/${str}`;
  };

  const isImageUrl = (url) =>
    /\.(png|jpe?g|webp|gif)(\?|$)/i.test(String(url || ""));

  const openPreview = (url) => {
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const renderImage = (value) => {
    const url = resolveFileUrl(value);
    if (!url) return "-";
    if (!isImageUrl(url)) {
      return (
        <Typography.Link href={url} target="_blank" rel="noreferrer">
          Open file
        </Typography.Link>
      );
    }
    return (
      <button
        type="button"
        onClick={() => openPreview(url)}
        style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
      >
        <img
          src={url}
          alt="uploaded"
          style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8 }}
        />
      </button>
    );
  };

  return (
    <Drawer
      title={
        <Space>
          <ApartmentOutlined />
          <span>{title}</span>
        </Space>
      }
      width={fullWidth ? "100vw" : 620}
      styles={{
        header: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
        body: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
        footer: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
      }}
      open={open}
      onClose={onClose}
      destroyOnClose
      placement="right"
      zIndex={2300}
      extra={
        <Space>
          <Button
            type="text"
            aria-label={fullWidth ? "Normal drawer width" : "Expand drawer"}
            title={fullWidth ? "Normal width" : "Expand"}
            icon={fullWidth ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={() => setFullWidth((v) => !v)}
          />
          <Button
            type="primary"
            icon={<EditOutlined />}
            disabled={!location?.id}
            onClick={() => onEdit?.(location)}
          >
            Edit
          </Button>
        </Space>
      }
      footer={
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
    >
      <Modal
        open={previewOpen}
        title="Preview"
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        zIndex={2450}
        centered
      >
        <img src={previewUrl} alt="preview" style={{ width: "100%", borderRadius: 10 }} />
      </Modal>
      <Descriptions bordered size="small" column={1} labelStyle={{ width: 180 }}>
        <Descriptions.Item label="Name">{location?.name || "-"}</Descriptions.Item>
        <Descriptions.Item label="Status">
          {location?.status ? (
            <Tag>{formatEnumLabel(location.status)}</Tag>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Location Type">
          {location?.businessLocationType?.label ||
            location?.businessLocationType?.code ||
            "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Description">
          {location?.description || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Profile Photo">{renderImage(location?.profilePhoto)}</Descriptions.Item>
        <Descriptions.Item label="Address Line 1">
          {location?.locationInformation?.addressLine1 || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Address Line 2">
          {location?.locationInformation?.addressLine2 || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="City">
          {location?.locationInformation?.city || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="County">
          {location?.locationInformation?.county || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="State">
          {location?.locationInformation?.state || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Country">
          {location?.locationInformation?.country || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Zip Code">
          {location?.locationInformation?.zipCode || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Latitude">
          {location?.locationInformation?.latitude ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Longitude">
          {location?.locationInformation?.longitude ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Place ID">
          {location?.locationInformation?.placeId || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Primary Email">
          {location?.contactInformation?.primaryEmail || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Primary Phone">
          {location?.contactInformation?.primaryPhone || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Website">
          {location?.contactInformation?.websiteUrl ? (
            <Typography.Text ellipsis>
              {location.contactInformation.websiteUrl}
            </Typography.Text>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Created At">
          {location?.createdAt ? formatDateTime(location.createdAt) : "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Updated At">
          {location?.updatedAt ? formatDateTime(location.updatedAt) : "-"}
        </Descriptions.Item>
      </Descriptions>
    </Drawer>
  );
}
